import { supabase } from './supabase';

const authService = {
  // Sign in with email and password
  signIn: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Sign up with email and password
  signUp: async (email, password, userData = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Sign in with Google
  signInWithGoogle: async () => {
    try {
      // Use environment variable or fallback to current origin
      const redirectUrl = import.meta.env.VITE_OAUTH_REDIRECT_URL || `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Sign in with Facebook
  signInWithFacebook: async () => {
    try {
      // Use environment variable or fallback to current origin
      const redirectUrl = import.meta.env.VITE_OAUTH_REDIRECT_URL || `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Sign out
  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get current session
  getSession: async () => {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get user profile
  getUserProfile: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update user profile
  updateUserProfile: async (userId, updates) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    try {
      // First verify the current password by attempting to sign in
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        return { success: false, error: 'No active session found' };
      }

      // Update the password
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update last login timestamp
  updateLastLoginAt: async (userId) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) {
        console.error('Error updating last login timestamp:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating last login timestamp:', error);
      return { success: false, error: error.message };
    }
  },

  // Auth state change listener
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  // API Key Management Functions
  generateApiKey: () => {
    // Generate a secure API key with clean format (similar to Bearer token format)
    // Using 29 random bytes to fit within VARCHAR(64) constraint (evapi + 58 hex chars = 63 total)
    const randomBytes = new Uint8Array(29);
    crypto.getRandomValues(randomBytes);
    const randomString = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `evapi${randomString}`;
  },

  createApiKey: async (userId) => {
    try {
      // Generate new API key
      const apiKey = authService.generateApiKey();

      // Store plain API key in database (no hashing needed)
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ api_key: apiKey })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // Return the plain API key
      return { success: true, data: { apiKey } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  revokeApiKey: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ api_key: null })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  getUserApiKey: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('api_key')
        .eq('id', userId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // Return whether user has an API key (not the actual key for security)
      return { 
        success: true, 
        data: { 
          hasApiKey: !!data.api_key,
          apiKeyMasked: data.api_key ? `evapi${'*'.repeat(32)}` : null
        } 
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  getActualApiKey: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('api_key')
        .eq('id', userId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // Return the actual API key for copying purposes
      return { 
        success: true, 
        data: { 
          apiKey: data.api_key
        } 
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  validateApiKey: async (apiKey) => {
    try {
      // Look up user by plain API key (no hashing needed)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, company_name')
        .eq('api_key', apiKey)
        .single();

      if (error || !data) {
        return { success: false, error: 'Invalid API key' };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },
};

export default authService;